import { baseApi as api } from './api.base.js';
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    apiControllerReloadApp: build.mutation<
      ApiControllerReloadAppApiResponse,
      ApiControllerReloadAppApiArg
    >({
      query: () => ({ url: `/web/reload`, method: 'POST' }),
    }),
    apiControllerChangeNode: build.mutation<
      ApiControllerChangeNodeApiResponse,
      ApiControllerChangeNodeApiArg
    >({
      query: (queryArg) => ({
        url: `/web/change_node/${queryArg.blockchainId}`,
        method: 'PUT',
      }),
    }),
    apiControllerSwitchPrivate: build.mutation<
      ApiControllerSwitchPrivateApiResponse,
      ApiControllerSwitchPrivateApiArg
    >({
      query: (queryArg) => ({
        url: `/web/switch_private/${queryArg.blockchainId}`,
        method: 'PUT',
      }),
    }),
    apiControllerGetBlockchains: build.query<
      ApiControllerGetBlockchainsApiResponse,
      ApiControllerGetBlockchainsApiArg
    >({
      query: () => ({ url: `/web/blockchains` }),
    }),
    apiControllerCreateBlockchain: build.mutation<
      ApiControllerCreateBlockchainApiResponse,
      ApiControllerCreateBlockchainApiArg
    >({
      query: (queryArg) => ({
        url: `/web/blockchains`,
        method: 'POST',
        body: queryArg.blockchainUpdateRequest,
      }),
    }),
    apiControllerGetBlockchain: build.query<
      ApiControllerGetBlockchainApiResponse,
      ApiControllerGetBlockchainApiArg
    >({
      query: (queryArg) => ({
        url: `/web/blockchains/${queryArg.blockchainId}`,
      }),
    }),
    apiControllerUpdateBlockchain: build.mutation<
      ApiControllerUpdateBlockchainApiResponse,
      ApiControllerUpdateBlockchainApiArg
    >({
      query: (queryArg) => ({
        url: `/web/blockchains/${queryArg.blockchainId}`,
        method: 'PUT',
        body: queryArg.blockchainUpdateRequest,
      }),
    }),
    apiControllerDeleteBlockchain: build.mutation<
      ApiControllerDeleteBlockchainApiResponse,
      ApiControllerDeleteBlockchainApiArg
    >({
      query: (queryArg) => ({
        url: `/web/blockchains/${queryArg.blockchainId}`,
        method: 'DELETE',
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as generatedApi };
export type ApiControllerReloadAppApiResponse =
  /** status 200 Reload scheduled successfully (response is returned before pm2 actually restarts the process) */ ReloadResponse;
export type ApiControllerReloadAppApiArg = void;
export type ApiControllerChangeNodeApiResponse =
  /** status 200 Node switched and connection reinitialized */ ChangeNodeResponse;
export type ApiControllerChangeNodeApiArg = {
  /** Blockchain ID */
  blockchainId: number;
};
export type ApiControllerSwitchPrivateApiResponse =
  /** status 200 Private RPC flag toggled */ SwitchPrivateResponse;
export type ApiControllerSwitchPrivateApiArg = {
  /** Blockchain ID */
  blockchainId: number;
};
export type ApiControllerGetBlockchainsApiResponse =
  /** status 200 Returns all active blockchains */ BlockchainsResponse;
export type ApiControllerGetBlockchainsApiArg = void;
export type ApiControllerCreateBlockchainApiResponse =
  /** status 200 Blockchain created */ BlockchainResponse;
export type ApiControllerCreateBlockchainApiArg = {
  blockchainUpdateRequest: BlockchainUpdateRequest;
};
export type ApiControllerGetBlockchainApiResponse =
  /** status 200 Returns blockchain */ BlockchainResponse;
export type ApiControllerGetBlockchainApiArg = {
  /** Blockchain ID */
  blockchainId: number;
};
export type ApiControllerUpdateBlockchainApiResponse =
  /** status 200 Blockchain updated */ BlockchainResponse;
export type ApiControllerUpdateBlockchainApiArg = {
  /** Blockchain ID */
  blockchainId: number;
  blockchainUpdateRequest: BlockchainUpdateRequest;
};
export type ApiControllerDeleteBlockchainApiResponse =
  /** status 200 Blockchain deleted */ BlockchainResponse;
export type ApiControllerDeleteBlockchainApiArg = {
  /** Blockchain ID */
  blockchainId: number;
};
export type ReloadResponse = {
  /** true if the detached pm2 reload was successfully scheduled */
  status: boolean;
  /** Human-readable status message */
  message?: string;
};
export type ChangeNodeData = {
  /** Blockchain ID */
  blockchainId: number;
  /** Active WS index after switch (1 or 2) */
  currentWs: number;
  /** Active WS endpoint URL */
  endpoint: string;
};
export type ChangeNodeResponse = {
  /** Operation status */
  status: boolean;
  data: ChangeNodeData;
};
export type SwitchPrivateData = {
  /** Blockchain ID */
  blockchainId: number;
  /** New flag value (0 or 1) */
  privateRPCOn: number;
};
export type SwitchPrivateResponse = {
  /** Operation status */
  status: boolean;
  data: SwitchPrivateData;
};
export type BlockchainItem = {
  id: number;
  name: string;
  /** Chain type: 0 EVM, 1 Solana, 2 TON, 3 Cosmos, 4 SUI, 5 Bittensor */
  type: number;
  testnet: number;
  scanUrl: string;
  privateRPC: string | null;
  privateRPCOn: number;
  ws: string | null;
  ws2: string | null;
  /** Active node slot: 1 or 2 */
  currentWs: number;
  rpc: string | null;
  freeRpc: string | null;
  freeRpc2: string | null;
  chainId: number;
  eip1559: number;
  deployGasLimit: number;
  approveGasLimit: number;
  transferGasLimit: number;
  swapMaxGasUSD: number | null;
  swapGasLimit: number;
  executorLowLimit: number;
  executorTopupMax: number;
  deployerLevelAlert: number;
  nativeTokenName: string;
  nativeUSDRate: number | null;
  averageSwapGasUsed: number | null;
  active: number;
};
export type BlockchainsResponse = {
  /** Operation status */
  status: boolean;
  data: BlockchainItem[];
};
export type BlockchainResponse = {
  /** Operation status */
  status: boolean;
  data: BlockchainItem;
};
export type BlockchainUpdateRequest = {
  name?: string;
  type?: number;
  testnet?: number;
  scanUrl?: string;
  privateRPC?: string | null;
  privateRPCOn?: number;
  ws?: string | null;
  ws2?: string | null;
  /** Active node slot: 1 or 2 */
  currentWs?: number;
  rpc?: string | null;
  freeRpc?: string | null;
  freeRpc2?: string | null;
  chainId?: number;
  eip1559?: number;
  deployGasLimit?: number;
  approveGasLimit?: number;
  transferGasLimit?: number;
  swapGasLimit?: number;
  swapMaxGasUSD?: number | null;
  averageSwapGasUsed?: number | null;
  executorLowLimit?: number;
  executorTopupMax?: number;
  deployerLevelAlert?: number;
  nativeTokenName?: string;
  nativeUSDRate?: number | null;
  active?: number;
};
